import React, { useMemo } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import type { ReferenceImage } from 'store/src/lib/tests/api/referenceImages';
import { parseReferenceImageHref } from './guidance-markdown';

interface GuidanceViewProps {
  markdown: string;
  referenceImages: ReferenceImage[];
  onOpenReferenceImage: (image: ReferenceImage) => void;
}

/**
 * Read-only renderer for Test Guidance markdown. Resolves `ref-image:{id}`
 * links to tappable chips (offline-safe, by id) and renders website links as
 * new-tab anchors. Unresolved reference links degrade to plain text.
 */
const GuidanceView: React.FC<GuidanceViewProps> = ({
  markdown,
  referenceImages,
  onOpenReferenceImage,
}) => {
  const byId = useMemo(
    () => new Map(referenceImages.map((img) => [img.id, img])),
    [referenceImages],
  );

  if (!markdown?.trim()) {
    return null;
  }

  return (
    <div className="max-w-none">
      <ReactMarkdown
        // Preserve the ref-image:{id} scheme (react-markdown otherwise strips
        // non-http URLs); keep default sanitisation for everything else.
        urlTransform={(url) =>
          url.startsWith('ref-image:') ? url : defaultUrlTransform(url)
        }
        components={{
          // @tailwindcss/typography (the `prose` classes) isn't installed, so
          // headings need explicit styling or they render indistinguishable
          // from body text (Tailwind Preflight resets h1-h6 to inherit).
          // Sized/weighted to match the other field labels on this popup
          // (FormLabelWithIndicator, text-sm/font-medium via the shared Label
          // primitive) rather than the much larger default markdown scale.
          h1: ({ children }) => <h1 className="text-base font-semibold">{children}</h1>,
          h2: ({ children }) => <h2 className="text-sm font-semibold">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm font-semibold">{children}</h3>,
          h4: ({ children }) => <h4 className="text-sm font-medium">{children}</h4>,
          h5: ({ children }) => <h5 className="text-sm font-medium">{children}</h5>,
          h6: ({ children }) => <h6 className="text-sm font-medium">{children}</h6>,
          p: ({ children }) => <p className="text-sm">{children}</p>,
          a: ({ href, children }) => {
            const refId = href ? parseReferenceImageHref(href) : null;

            if (refId !== null) {
              const image = byId.get(refId);
              if (!image) {
                // Unresolved reference — degrade to plain text rather than a dead link.
                return <>{children}</>;
              }
              return (
                <button
                  type="button"
                  className="text-primary underline underline-offset-2"
                  onClick={() => onOpenReferenceImage(image)}
                >
                  {children}
                </button>
              );
            }

            return (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            );
          },
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
};

export default GuidanceView;
